import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Plus, Search } from 'lucide-react';

import DataTable from '../ui/DataTable';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import TeamsService from "../../services/TeamsService"
import AnnouncementService from '../../services/AnnouncementService';
import AnnouncementMemberService from '../../services/AnnouncementMemberService';
import { processTeamData } from '../../utils/utils';

function TeamList() {

    const navigate = useNavigate();

    const [teams, setTeams] = useState([]);
    const [editOpen, setEditOpen] = useState(false);
    const [editName, setEditName] = useState('');
    const [editTeamId, setEditTeamId] = useState(null);

    // Activation dialog state
    const [activateOpen, setActivateOpen] = useState(false);
    const [activateType, setActivateType] = useState('announcement'); // 'announcement' | 'drill'
    const [activateItems, setActivateItems] = useState([]);
    const [selectedTeamId, setSelectedTeamId] = useState(null);

    const loadTeams = async () => {
        try {
            let response = await TeamsService.findAll(1, 100);
            if (response?.status === 200) {
                setTeams(processTeamData(response.data));
            }
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => { loadTeams(); }, []);

    const headCells = [
        { id: 'name', label: 'Name' },
        { id: 'totalMembers', label: 'Total' },
        { id: 'doctors', label: 'Doctors' },
        { id: 'admins', label: 'Admins' },
        { id: 'nurses', label: 'Nurses' },
        { id: 'rsos', label: 'RSOs' },
    ];

    const handleEdit = (row) => {
        setEditTeamId(row.id);
        setEditName(row.name);
        setEditOpen(true);
    };

    const handleEditSave = async () => {
        if (!editName.trim()) {
            toast.info("Please enter a team name");
            return;
        }
        try {
            await TeamsService.update({ id: editTeamId, name: editName.trim() });
            toast.success("Team updated successfully");
            setEditOpen(false);
            loadTeams();
        } catch (error) {
            toast.error("Failed to update team");
        }
    };

    const handleDelete = async (row) => {
        if (!window.confirm(`Delete team "${row.name}"?`)) return;
        try {
            await TeamsService.delete(row.id);
            toast.success("Team deleted successfully");
            loadTeams();
        } catch (error) {
            toast.error("Failed to delete team");
        }
    };

    const handleView = (row) => {
        navigate(`${row.id}`);
    };

    const openActivateDialog = async (type, row) => {
        setActivateType(type);
        setSelectedTeamId(row.id);
        try {
            const response = await AnnouncementService.findAll(1, 100);
            if (response?.status === 200) {
                const isDrill = type === 'drill';
                setActivateItems(response.data.filter(a => a.drill === isDrill));
            }
        } catch (error) {
            toast.error("Failed to load " + type + "s");
        }
        setActivateOpen(true);
    };

    const handleActivateSelect = async (announcementId) => {
        try {
            const response = await AnnouncementMemberService.activateAll(announcementId, selectedTeamId);
            toast.success(response.message || "Team activated successfully");
            setActivateOpen(false);
        } catch (error) {
            toast.error(error?.response?.data?.message || "Activation failed");
        }
    };

    const options = [
        { title: 'Activate Announcement', handler: (row) => openActivateDialog('announcement', row) },
        { title: 'Activate Drill', handler: (row) => openActivateDialog('drill', row) },
    ];

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="relative w-full sm:w-80">
                    <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                        type="search"
                        placeholder="Search teams..."
                        className="form-input pl-9"
                    />
                </div>
                <Button icon={Plus} onClick={() => navigate("add")}>New Team</Button>
            </div>

            <DataTable
                title="EMS Teams"
                rows={teams}
                headCells={headCells}
                moreOptions={options}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onView={handleView}
            />

            {/* Edit Team Modal */}
            <Modal
                open={editOpen}
                onClose={() => setEditOpen(false)}
                title="Edit Team"
                onConfirm={handleEditSave}
                confirmLabel="Save"
            >
                <div>
                    <label className="form-label">Team Name</label>
                    <input
                        className="form-input"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                    />
                </div>
            </Modal>

            {/* Activate Announcement/Drill Modal */}
            <Modal
                open={activateOpen}
                onClose={() => setActivateOpen(false)}
                title={`Select ${activateType === 'drill' ? 'Drill' : 'Announcement'} to Activate`}
                footer={null}
            >
                <div className="space-y-2 max-h-96 overflow-y-auto">
                    {activateItems.length === 0 && (
                        <p className="text-sm text-neutral-400 text-center py-6">No {activateType}s available</p>
                    )}
                    {activateItems.map(item => (
                        <button
                            key={item.id}
                            onClick={() => handleActivateSelect(item.id)}
                            className="w-full text-left px-4 py-3 rounded-lg border border-neutral-200 hover:border-seha-300 hover:bg-seha-50 transition-colors"
                        >
                            <p className="text-sm font-bold text-neutral-800">{item.title}</p>
                            <p className="text-xs text-neutral-500 mt-0.5">
                                {item.location || 'No location'} &mdash; {item.incident_type?.incident_type_name || 'N/A'}
                            </p>
                        </button>
                    ))}
                </div>
            </Modal>
        </div>
    )
}

export default TeamList
